import React from 'react';
import { CustomerCategory } from '../../types';

interface CustomerAddFormProps {
  isAdding: boolean;
  categories: CustomerCategory[];
  formData: {
    name: string;
    legalName: string;
    tin: string;
    email: string;
    phoneNumber: string;
    company: string;
    charterDiscount: number;
    scheduleDiscount: number;
    creditLimit: number;
    categoryId: string;
    createInZoho: boolean;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<{
      name: string;
      legalName: string;
      tin: string;
      email: string;
      phoneNumber: string;
      company: string;
      charterDiscount: number;
      scheduleDiscount: number;
      creditLimit: number;
      categoryId: string;
      createInZoho: boolean;
    }>
  >;
  editCurrency: string;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

const CustomerAddForm: React.FC<CustomerAddFormProps> = ({
  isAdding,
  categories,
  formData,
  setFormData,
  editCurrency,
  onCancel,
  onSubmit,
}) => {
  if (!isAdding) return null;

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-10 shadow-xl animate-in slide-in-from-top-4">
      <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest mb-8">New Entity Onboarding</h3>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Display Name *</label>
          <input
            required
            type="text"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Legal Registered Name</label>
          <input
            type="text"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.legalName}
            onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Email</label>
          <input
            type="email"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Phone Number</label>
          <input
            type="tel"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Company</label>
          <input
            type="text"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">TIN / VAT</label>
          <input
            type="text"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.tin}
            onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Discount Group</label>
          <select
            value={formData.categoryId}
            onChange={(e) => {
              const categoryId = e.target.value;
              setFormData({ ...formData, categoryId });
              if (categoryId) {
                const category = categories.find((c) => c.id === categoryId);
                if (category) {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId,
                    charterDiscount: category.defaultCharterDiscount,
                    scheduleDiscount: category.defaultScheduleDiscount,
                    creditLimit: category.defaultCreditLimit,
                  }));
                }
              }
            }}
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
          >
            <option value="">No Group (Manual Terms)</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Zoho Books</label>
          <label className="flex items-center gap-3 bg-slate-50 rounded-xl p-4 font-bold text-sm text-slate-700">
            <input
              type="checkbox"
              checked={formData.createInZoho}
              onChange={(e) => setFormData({ ...formData, createInZoho: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300"
            />
            Create contact in Zoho Books
          </label>
          <p className="text-[10px] text-slate-400">Required for finance tracking.</p>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Charter Disc %</label>
          <input
            type="number"
            min="0"
            max="100"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.charterDiscount}
            onChange={(e) => setFormData({ ...formData, charterDiscount: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Schedule Disc %</label>
          <input
            type="number"
            min="0"
            max="100"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.scheduleDiscount}
            onChange={(e) => setFormData({ ...formData, scheduleDiscount: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Credit Limit ({editCurrency})</label>
          <input
            type="number"
            min="0"
            className="w-full bg-slate-50 border-none rounded-xl p-4 font-bold text-slate-800"
            value={formData.creditLimit}
            onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="md:col-span-3 flex justify-end gap-4 mt-4">
          <button type="button" onClick={onCancel} className="px-8 py-3 text-[10px] font-black uppercase text-slate-400 hover:text-slate-600">
            Cancel
          </button>
          <button type="submit" className="px-8 py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest">
            Commit Entity
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerAddForm;
